import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'components/ui/badge';
import { cn } from 'lib/utils';
import { MIN_ANSWERS_FOR_MASTERY } from 'modules/studyPlan';
import { examKey, subjectKey } from 'modules/studyPlan/labels';
import type { StudyPlan } from 'modules/studyPlan';

const titleCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

/** Mastery by topic, weakest first. A topic with few answers is marked low data and drawn quiet. One per exam when there are several (`exam`). */
export function TopicsCard({ mastery, targetPercent, exam }: { mastery: StudyPlan['mastery']; targetPercent: number; exam?: string }) {
  const { t } = useTranslation();
  const headingId = exam ? `plan-topics-${exam}` : 'plan-topics';
  const title = exam ? `${t('studyPlan.topics_title')} · ${t(examKey(exam), { defaultValue: exam })}` : t('studyPlan.topics_title');

  return (
    <section aria-labelledby={headingId} className="rounded-lg border border-border bg-card p-5 md:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 id={headingId} className="text-[15px] font-semibold text-foreground">{title}</h2>
        <span className="text-[12.5px] text-muted-foreground">{t('studyPlan.topics_hint', { target: targetPercent })}</span>
      </div>

      {mastery.groups.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">{t('studyPlan.topics_empty')}</p>
      ) : (
        <>
          {mastery.groups.map((group) => (
            <div key={group.subject} className="mt-6 first:mt-4">
              <h3 className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm font-bold text-foreground">
                <span className="h-4 w-1 shrink-0 rounded-full bg-foreground" aria-hidden="true" />
                <span className="min-w-0 break-words">{t(subjectKey(group.subject), { defaultValue: titleCase(group.subject) })}</span>
              </h3>
              <ul className="mt-1">
                {group.topics.map((topic) => (
                  <li key={topic.topic} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 border-t border-border py-3 md:grid-cols-[150px_minmax(0,1fr)_44px_92px]">
                    <span className={cn('break-words text-sm md:truncate', !topic.lowData && topic.mastery < 50 ? 'font-semibold text-foreground' : 'font-medium text-foreground/90')}>{topic.topic}</span>
                    <span className="text-right text-sm font-semibold tabular-nums md:order-3">{topic.mastery}%</span>
                    <div className="relative col-span-2 h-2 rounded-full bg-muted md:order-2 md:col-span-1" role="img" aria-label={`${topic.topic}: ${topic.mastery}%`}>
                      <div
                        className={cn('h-full rounded-full', topic.lowData ? 'bg-muted-foreground/40' : topic.mastery < 50 ? 'bg-error-600' : 'bg-foreground')}
                        style={{ width: `${topic.mastery}%` }}
                      />
                      <span className="absolute -bottom-[3px] -top-[3px] w-0.5 rounded-sm bg-foreground" style={{ left: `${targetPercent}%` }} aria-hidden="true" />
                    </div>
                    <span className="hidden text-right text-xs text-muted-foreground md:order-4 md:block">
                      {topic.lowData ? (
                        <Badge variant="warning">{t('studyPlan.low_data')}</Badge>
                      ) : (
                        t('studyPlan.topics_answers', { count: topic.answers })
                      )}
                    </span>
                    {topic.lowData && <span className="text-xs font-medium text-warning-700 dark:text-warning-400 md:hidden">{t('studyPlan.low_data')} · {t('studyPlan.topics_answers', { count: topic.answers })}</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p className="mt-4 text-[12.5px] text-muted-foreground">{t('studyPlan.topics_low_note', { count: MIN_ANSWERS_FOR_MASTERY })}</p>
        </>
      )}
    </section>
  );
}

export default TopicsCard;
